// ---------------------------------------------------------------------------
// reorderable-list — generic drag-to-reorder wiring for a key-keyed item list
// ---------------------------------------------------------------------------
//
// Splits drag origin (a dedicated HANDLE inside each item) from the drop target
// (the item ROOT), so items containing interactive/text controls don't hijack
// selection on drag. Owns NO order state — it reads keys live via getKeys() and
// reports a move by (fromKey, toKey); the caller mutates and re-renders.

import { computeDropIndex } from './compute-drop-index.js';

/** Attribute carrying an item's stable key. */
const KEY_ATTR = 'data-reorder-key';

/**
 * @param {HTMLElement} container - element delegating drag events for its items.
 * @param {{
 *   itemSelector: string,
 *   handleSelector: string,
 *   getKeys: () => string[],
 *   onMove: (fromKey: string, toKey: string) => void,
 *   isBlocked?: () => boolean,
 * }} options
 * @returns {{ destroy: () => void }}
 */
export function attachReorderable(container, options) {
  const { itemSelector, handleSelector, getKeys, onMove, isBlocked } = options;

  let draggedKey = '';

  /** @param {HTMLElement} el @returns {string} */
  const keyOf = (el) => el.getAttribute(KEY_ATTR) ?? '';

  /** @param {EventTarget | null} target @returns {HTMLElement | null} */
  const itemFrom = (target) =>
    target instanceof HTMLElement ? /** @type {HTMLElement|null} */ (target.closest(itemSelector)) : null;

  const clearOver = () => {
    for (const el of container.querySelectorAll('.is-drop-target')) {
      el.classList.remove('is-drop-target');
    }
  };

  /** @param {DragEvent} e */
  const onDragStart = (e) => {
    if (isBlocked?.()) return;
    const target = e.target;
    if (!(target instanceof HTMLElement)) return;
    // Drag must originate on the handle (or a descendant of it).
    if (!target.closest(handleSelector)) return;
    const item = itemFrom(target);
    if (!item) return;
    draggedKey = keyOf(item);
    item.classList.add('is-dragging');
    item.setAttribute('aria-grabbed', 'true');
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', draggedKey);
    }
  };

  /** @param {DragEvent} e */
  const onDragOver = (e) => {
    if (!draggedKey) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
  };

  /** @param {DragEvent} e */
  const onDragEnter = (e) => {
    if (!draggedKey) return;
    const item = itemFrom(e.target);
    if (!item) return;
    const key = keyOf(item);
    if (!key || key === draggedKey) return;
    clearOver();
    item.classList.add('is-drop-target');
  };

  /** @param {DragEvent} e */
  const onDrop = (e) => {
    if (!draggedKey) return;
    e.preventDefault();
    const item = itemFrom(e.target);
    const keys = getKeys();
    const toIndex = computeDropIndex(keys, draggedKey, item, keyOf);
    const fromIndex = keys.indexOf(draggedKey);
    if (toIndex >= 0 && fromIndex >= 0 && toIndex !== fromIndex) {
      onMove(draggedKey, keys[toIndex]);
    }
    clearOver();
  };

  const onDragEnd = () => {
    for (const el of container.querySelectorAll('.is-dragging')) {
      el.classList.remove('is-dragging');
      el.removeAttribute('aria-grabbed');
    }
    clearOver();
    draggedKey = '';
  };

  container.addEventListener('dragstart', onDragStart);
  container.addEventListener('dragover', onDragOver);
  container.addEventListener('dragenter', onDragEnter);
  container.addEventListener('drop', onDrop);
  container.addEventListener('dragend', onDragEnd);

  return {
    destroy() {
      container.removeEventListener('dragstart', onDragStart);
      container.removeEventListener('dragover', onDragOver);
      container.removeEventListener('dragenter', onDragEnter);
      container.removeEventListener('drop', onDrop);
      container.removeEventListener('dragend', onDragEnd);
      clearOver();
      draggedKey = '';
    },
  };
}

export { KEY_ATTR as REORDER_KEY_ATTR };
